import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Minus, Plus, ShoppingBag, Heart, Truck, RotateCcw, Shield, ChevronRight } from 'lucide-react';
import { products } from '../data/products';
import { useCart } from '../context/CartContext';
import { ProductCardGrid } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export const Product = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  const product = products.find(p => p.id === parseInt(productId));

  if (!product) {
    return (
      <div className="pt-32 pb-24 text-center">
        <h1 className="font-['Cormorant_Garamond'] text-3xl text-[#2D4A3E]">Product not found</h1>
        <Link to="/category/all" className="text-[#C67D4E] mt-4 inline-block font-['Montserrat']">
          Back to Shop
        </Link>
      </div>
    );
  }

  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${product.name} added to cart`, {
      description: `Quantity: ${quantity}`,
      action: {
        label: 'View Cart',
        onClick: () => navigate('/cart')
      }
    });
  };

  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate('/checkout');
  };

  return (
    <main className="pt-24 pb-24 bg-[#FAF8F5]" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6">
        <nav className="flex items-center gap-2 text-sm font-['Montserrat']" data-testid="breadcrumb">
          <Link to="/" className="text-[#8B7355] hover:text-[#2D4A3E]">Home</Link>
          <ChevronRight size={14} className="text-[#E8E4DE]" />
          <Link to={`/category/${product.category}`} className="text-[#8B7355] hover:text-[#2D4A3E] capitalize">
            {product.category.replace('-', ' ')}
          </Link>
          <ChevronRight size={14} className="text-[#E8E4DE]" />
          <span className="text-[#2D4A3E]">{product.name}</span>
        </nav>
      </div>

      {/* Product Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            {/* Main Image */}
            <div className="aspect-square sm:aspect-[4/5] bg-white border border-[#E8E4DE] overflow-hidden">
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover object-center"
                data-testid="product-main-image"
              />
            </div>

            {/* Thumbnails */}
            {product.images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-16 h-16 sm:w-20 sm:h-20 bg-white border-2 overflow-hidden flex-shrink-0 transition-colors ${
                      selectedImage === index ? 'border-[#C67D4E]' : 'border-[#E8E4DE] hover:border-[#8B7355]'
                    }`}
                    data-testid={`product-thumbnail-${index}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:py-4"
          >
            {/* Badge */}
            {product.badge && (
              <span className={`inline-block px-3 py-1.5 text-xs uppercase tracking-wider font-semibold mb-4 ${
                product.badge === 'Best Seller' 
                  ? 'bg-[#C67D4E] text-white' 
                  : product.badge === 'Premium'
                  ? 'bg-[#2D4A3E] text-white'
                  : 'bg-[#8B7355] text-white'
              }`}>
                {product.badge}
              </span>
            )}

            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl text-[#2D4A3E] leading-tight" data-testid="product-title">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    className={i < Math.floor(product.rating) ? 'fill-[#C67D4E] text-[#C67D4E]' : 'text-[#E8E4DE]'}
                  />
                ))}
              </div>
              <span className="font-['Montserrat'] text-sm text-[#8B7355]">
                {product.rating} ({product.reviews} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-4 mt-6">
              <span className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-semibold text-[#2D4A3E]" data-testid="product-price">
                ${product.price.toLocaleString()}
              </span>
              {product.originalPrice && product.originalPrice > product.price && (
                <>
                  <span className="font-['Montserrat'] text-lg text-[#8B7355] line-through">
                    ${product.originalPrice.toLocaleString()}
                  </span>
                  <span className="text-sm font-semibold text-[#10B981] font-['Montserrat']">
                    Save ${(product.originalPrice - product.price).toLocaleString()}
                  </span>
                </>
              )}
            </div>

            {/* Description */}
            <p className="font-['Montserrat'] text-[#5A6B5D] leading-relaxed mt-6" data-testid="product-description">
              {product.description}
            </p>

            {/* Quantity & Add to Cart */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-6">
                <span className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Quantity</span>
                <div className="flex items-center border border-[#E8E4DE] bg-white">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-[#FAF8F5] transition-colors"
                    data-testid="quantity-decrease"
                  >
                    <Minus size={16} className="text-[#2D4A3E]" />
                  </button>
                  <span className="px-6 font-['Montserrat'] font-semibold text-[#2D4A3E]" data-testid="quantity-display">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 hover:bg-[#FAF8F5] transition-colors"
                    data-testid="quantity-increase"
                  >
                    <Plus size={16} className="text-[#2D4A3E]" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleAddToCart}
                  className="flex-1 bg-[#2D4A3E] text-white hover:bg-[#1E332A] py-6 rounded-sm text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2 font-['Montserrat']"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag size={18} />
                  Add to Cart
                </Button>
                <Button
                  onClick={handleBuyNow}
                  className="flex-1 bg-[#C67D4E] text-white hover:bg-[#A86840] py-6 rounded-sm text-xs uppercase tracking-wider font-semibold font-['Montserrat']"
                  data-testid="buy-now-btn"
                >
                  Buy Now
                </Button>
              </div>

              <Button
                variant="outline"
                className="w-full border-[#E8E4DE] py-4 rounded-sm text-xs uppercase tracking-wider font-semibold flex items-center justify-center gap-2 hover:border-[#C67D4E] hover:text-[#C67D4E] font-['Montserrat']"
                data-testid="wishlist-btn"
              >
                <Heart size={18} />
                Add to Wishlist
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="mt-8 pt-8 border-t border-[#E8E4DE] grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto bg-[#C67D4E]/10 rounded-full flex items-center justify-center mb-2">
                  <Truck className="text-[#C67D4E]" size={18} />
                </div>
                <p className="font-['Montserrat'] text-xs text-[#5A6B5D]">Free Shipping</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto bg-[#C67D4E]/10 rounded-full flex items-center justify-center mb-2">
                  <RotateCcw className="text-[#C67D4E]" size={18} />
                </div>
                <p className="font-['Montserrat'] text-xs text-[#5A6B5D]">30-Day Returns</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto bg-[#C67D4E]/10 rounded-full flex items-center justify-center mb-2">
                  <Shield className="text-[#C67D4E]" size={18} />
                </div>
                <p className="font-['Montserrat'] text-xs text-[#5A6B5D]">Secure Checkout</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Product Details Tabs */}
        <div className="mt-16 md:mt-20">
          <Tabs defaultValue="specifications" className="w-full">
            <TabsList className="w-full justify-start border-b border-[#E8E4DE] bg-transparent rounded-none h-auto p-0 overflow-x-auto">
              <TabsTrigger 
                value="specifications"
                className="font-['Montserrat'] text-xs uppercase tracking-wider px-4 sm:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-[#C67D4E] data-[state=active]:text-[#2D4A3E] data-[state=active]:bg-transparent whitespace-nowrap"
                data-testid="tab-specifications"
              >
                Specifications
              </TabsTrigger>
              <TabsTrigger 
                value="care"
                className="font-['Montserrat'] text-xs uppercase tracking-wider px-4 sm:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-[#C67D4E] data-[state=active]:text-[#2D4A3E] data-[state=active]:bg-transparent whitespace-nowrap"
                data-testid="tab-care"
              >
                Care Instructions
              </TabsTrigger>
              <TabsTrigger 
                value="shipping"
                className="font-['Montserrat'] text-xs uppercase tracking-wider px-4 sm:px-6 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-[#C67D4E] data-[state=active]:text-[#2D4A3E] data-[state=active]:bg-transparent whitespace-nowrap"
                data-testid="tab-shipping"
              >
                Shipping & Returns
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="specifications" className="pt-8 bg-white p-6 border border-t-0 border-[#E8E4DE]">
              <div className="max-w-2xl">
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">Product Specifications</h3>
                <div className="space-y-4">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex border-b border-[#E8E4DE] pb-4">
                      <span className="font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355] w-32 sm:w-40 flex-shrink-0 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className="font-['Montserrat'] text-[#2D4A3E] text-sm">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="care" className="pt-8 bg-white p-6 border border-t-0 border-[#E8E4DE]">
              <div className="max-w-2xl">
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">Care Instructions</h3>
                <p className="font-['Montserrat'] text-[#5A6B5D] leading-relaxed">{product.care}</p>
              </div>
            </TabsContent>
            
            <TabsContent value="shipping" className="pt-8 bg-white p-6 border border-t-0 border-[#E8E4DE]">
              <div className="max-w-2xl">
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">Shipping & Returns</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-['Montserrat'] font-semibold text-[#2D4A3E] mb-2">Free Shipping</h4>
                    <p className="font-['Montserrat'] text-sm text-[#5A6B5D]">
                      Enjoy free standard shipping on all orders over $999. Standard delivery takes 7-14 business days.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-['Montserrat'] font-semibold text-[#2D4A3E] mb-2">White Glove Delivery</h4>
                    <p className="font-['Montserrat'] text-sm text-[#5A6B5D]">
                      Premium white glove delivery available. Includes in-home placement and packaging removal.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-['Montserrat'] font-semibold text-[#2D4A3E] mb-2">30-Day Returns</h4>
                    <p className="font-['Montserrat'] text-sm text-[#5A6B5D]">
                      Not satisfied? Return within 30 days for a full refund. Item must be in original condition.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-20 bg-white mt-20 border-t border-[#E8E4DE]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-10"
            >
              <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl md:text-4xl text-[#2D4A3E]">
                You May Also Like
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {relatedProducts.map((product, index) => (
                <ProductCardGrid key={product.id} product={product} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sticky Add to Cart - Mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E8E4DE] p-4 lg:hidden z-40" data-testid="sticky-cart">
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-['Cormorant_Garamond'] text-base text-[#2D4A3E] truncate">{product.name}</p>
            <p className="font-['Montserrat'] font-semibold text-[#C67D4E]">${product.price.toLocaleString()}</p>
          </div>
          <Button
            onClick={handleAddToCart}
            className="bg-[#2D4A3E] text-white hover:bg-[#1E332A] px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold font-['Montserrat'] flex-shrink-0"
            data-testid="sticky-add-to-cart"
          >
            Add to Cart
          </Button>
        </div>
      </div>
    </main>
  );
};
